// Key cabinets for the administrator: slots and the car on each key, linking a plate to a slot,
// the cabinet's events, and the simulator that asks the service what a real cabinet asks.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sv } from '@aldorock-fordon/shared/sv';
import { api, ApiError } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { Facts } from '../ui/Facts.tsx';
import { Table } from '../ui/Table.tsx';
import { TextField } from '../ui/Fields.tsx';
import { Status } from '../ui/Status.tsx';
import { moment, plate } from '../format.ts';

type Cabinet = {
  id: string;
  name: string;
  vendor: string;
  location: string | null;
  status: string;
  lastContactAt: string | null;
  slotCount: number;
  pool: { id: string; name: string | null } | null;
  slots: Array<{
    id: string;
    slotNumber: number;
    vehicleId: string | null;
    plate: string | null;
    make: string | null;
    model: string | null;
    keyPresent: boolean;
  }>;
  events: Array<{
    id: string;
    occurredAt: string;
    kind: string;
    slot: number | null;
    plate: string | null;
    user: string | null;
    bookingNumber: string | null;
  }>;
};

function SlotLink({ cabinetId, slot }: { cabinetId: string; slot: Cabinet['slots'][number] }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(slot.plate ?? '');
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: (p: string | null) =>
      api(`/keys/slots/${slot.id}`, { method: 'PUT', body: JSON.stringify({ plate: p }) }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['cabinets'] });
      setOpen(false);
      setError(undefined);
    },
    onError: (err) =>
      setError(err instanceof ApiError && err.errors[0] ? err.errors[0].message : sv.errors.server),
  });
  void cabinetId;
  if (!open) {
    return (
      <span className="actions-inline">
        <button
          className="btn btn-quiet"
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`${sv.keys.link} ${sv.keys.slot.toLowerCase()} ${slot.slotNumber}`}
        >
          {sv.keys.link}
        </button>
        {slot.vehicleId ? (
          <button
            className="btn btn-quiet"
            type="button"
            onClick={() => save.mutate(null)}
            aria-label={`${sv.keys.unlink} ${sv.keys.slot.toLowerCase()} ${slot.slotNumber}`}
          >
            {sv.keys.unlink}
          </button>
        ) : null}
      </span>
    );
  }
  return (
    <form
      className="label-row row"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(value.trim() || null);
      }}
    >
      <TextField
        id={`slot-${slot.id}`}
        label={sv.keys.linkLabel(slot.slotNumber)}
        value={value}
        error={error}
        onChange={setValue}
      />
      <button className="btn btn-primary" type="submit" disabled={save.isPending}>
        {sv.keys.linkButton}
      </button>
      <button className="btn" type="button" onClick={() => setOpen(false)}>
        {sv.vehicleCard.cancelEdit}
      </button>
    </form>
  );
}

function Simulator({ cabinet }: { cabinet: Cabinet }) {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const use = useMutation({
    mutationFn: (action: 'uttag' | 'aterlamning') =>
      api<{ message: string }>(`/keys/cabinets/${cabinet.id}/code`, {
        method: 'POST',
        body: JSON.stringify({ code: code.trim(), action }),
      }),
    onSuccess: async (r) => {
      setResult({ ok: true, message: r.message });
      await qc.invalidateQueries({ queryKey: ['cabinets'] });
    },
    onError: async (err) => {
      setResult({
        ok: false,
        message: err instanceof ApiError && err.message ? err.message : sv.errors.server,
      });
      await qc.invalidateQueries({ queryKey: ['cabinets'] });
    },
  });
  return (
    <form
      className="form-block simulator"
      aria-labelledby={`sim-${cabinet.id}`}
      onSubmit={(e) => {
        e.preventDefault();
        use.mutate('uttag');
      }}
    >
      <h3 className="form-heading" id={`sim-${cabinet.id}`}>
        {sv.keys.simulator}
      </h3>
      <p className="muted">{sv.keys.simulatorIntro}</p>
      <TextField id={`code-${cabinet.id}`} label={sv.keys.code} value={code} onChange={setCode} />
      <div className="form-actions">
        <button
          className="btn btn-primary"
          type="submit"
          disabled={use.isPending || !/^\d{6}$/.test(code.trim())}
        >
          {sv.keys.takeKey}
        </button>
        <button
          className="btn"
          type="button"
          disabled={use.isPending || !/^\d{6}$/.test(code.trim())}
          onClick={() => use.mutate('aterlamning')}
        >
          {sv.keys.returnKey}
        </button>
      </div>
      {result ? (
        <p
          className="notice"
          data-state={result.ok ? 'done' : 'bad'}
          role="status"
          data-testid={`sim-result-${cabinet.id}`}
        >
          {result.message}
        </p>
      ) : null}
    </form>
  );
}

export function KeysPage() {
  const h1 = usePage(sv.keys.title);
  const q = useQuery({ queryKey: ['cabinets'], queryFn: () => api<Cabinet[]>('/keys/cabinets') });
  return (
    <>
      <PageHead help="keys" title={sv.keys.title} titleRef={h1} />
      <p>{sv.keys.intro}</p>
      {q.isPending ? <p role="status">{sv.app.loading}</p> : null}
      {q.data?.map((c) => (
        <section className="block" key={c.id} aria-labelledby={`cab-${c.id}`}>
          <h2 className="block-title" id={`cab-${c.id}`}>
            {c.name}
          </h2>
          <Facts
            top={false}
            facts={[
              { key: 'pool', label: sv.columns.pool, value: c.pool?.name ?? sv.none },
              {
                key: 'vendor',
                label: sv.keys.cabinet,
                value: `${sv.keys.vendor[c.vendor] ?? c.vendor}, ${c.slotCount} ${sv.keys.slot.toLowerCase()}er`,
              },
              { key: 'loc', label: sv.pools.address, value: c.location ?? sv.none },
              {
                key: 'st',
                label: sv.columns.status,
                value: (
                  <Status tone={c.status === 'ok' ? 'done' : c.status === 'fel' ? 'late' : 'none'}>
                    {sv.keys.status[c.status] ?? c.status}
                  </Status>
                ),
              },
              {
                key: 'lc',
                label: sv.keys.lastContact,
                value: c.lastContactAt ? moment(c.lastContactAt) : sv.none,
              },
            ]}
          />
          <Table caption={sv.keys.slots(c.name)} hasOptional>
            <thead>
              <tr>
                <th scope="col" className="r">
                  {sv.keys.slot}
                </th>
                <th scope="col">{sv.columns.vehicle}</th>
                <th scope="col">{sv.columns.key}</th>
                <th scope="col">{sv.columns.action}</th>
              </tr>
            </thead>
            <tbody>
              {c.slots.map((s) => (
                <tr key={s.id}>
                  <td className="r">{s.slotNumber}</td>
                  <td>
                    {s.plate ? (
                      `${[s.make, s.model].filter(Boolean).join(' ')} ${plate(s.plate)}`
                    ) : (
                      <span className="muted">{sv.keys.empty}</span>
                    )}
                  </td>
                  <td>
                    {s.plate ? (
                      <Status tone={s.keyPresent ? 'done' : 'waiting'}>
                        {s.keyPresent ? sv.keys.keyIn : sv.keys.keyOut}
                      </Status>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    <SlotLink cabinetId={c.id} slot={s} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {c.vendor === 'simulator' ? <Simulator cabinet={c} /> : null}
          <Table
            caption={sv.keys.events}
            isEmpty={c.events.length === 0}
            empty={sv.keys.eventsEmpty}
            hasOptional
          >
            <thead>
              <tr>
                <th scope="col">{sv.columns.time}</th>
                <th scope="col">{sv.columns.event}</th>
                <th scope="col" className="r col-opt">
                  {sv.keys.slot}
                </th>
                <th scope="col">{sv.columns.vehicle}</th>
                <th scope="col" className="col-opt">
                  {sv.columns.who}
                </th>
                <th scope="col" className="col-opt">
                  {sv.columns.number}
                </th>
              </tr>
            </thead>
            <tbody>
              {c.events.map((e) => (
                <tr key={e.id}>
                  <td className="num">{moment(e.occurredAt)}</td>
                  <td>{sv.keys.eventKinds[e.kind] ?? e.kind}</td>
                  <td className="r col-opt">{e.slot ?? ''}</td>
                  <td>{e.plate ? plate(e.plate) : ''}</td>
                  <td className="col-opt">{e.user ?? ''}</td>
                  <td className="col-opt">{e.bookingNumber ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </section>
      ))}
    </>
  );
}
